import React, { useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { Box, Button, IconButton, Tooltip, Typography, Modal, Pagination } from '@mui/material';
import { Add, Delete, Visibility } from '@mui/icons-material';
import { RequisitionService } from '../../services/requisition.service';
import { Requisition, REQUISITION_STATUS_LABELS } from '../../interfaces/requisition.interface';
import { useToast } from '../../context/ToastContext';
import ScrollableTable from '../common/ScrollableTable';
import { helpWhy } from '../../help/why';

const tableHeaderStyle = {
    padding: '12px',
    textAlign: 'left' as const,
    backgroundColor: '#f5f5f5',
    borderBottom: '1px solid #ddd'
};

const tableCellStyle = {
    padding: '12px',
    borderBottom: '1px solid #ddd'
};

const MyRequisitions: React.FC = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const [requisitions, setRequisitions] = useState<Requisition[]>([]);
    const [loading, setLoading] = useState(true);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedRequisition, setSelectedRequisition] = useState<string | null>(null);
    const [pageInfo, setPageInfo] = useState({
        currentPage: 0,
        totalPages: 0,
        totalElements: 0
    });

    const fetchRequisitions = async (page: number = 0) => {
        try {
            setLoading(true);
            const response = await RequisitionService.getLoggedInUserRequisitions(page);
            setRequisitions(response.content);
            setPageInfo({
                currentPage: response.number,
                totalPages: response.totalPages,
                totalElements: response.totalElements
            });
        } catch (err) {
            toast.showApiError(err, 'Failed to load requisitions');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRequisitions();
    }, []);

    const handleDelete = async () => {
        if (!selectedRequisition) return;

        try {
            await RequisitionService.deleteRequisition(selectedRequisition);
            toast.showSuccess('Requisition deleted');
            await fetchRequisitions(pageInfo.currentPage);
            setDeleteModalOpen(false);
            setSelectedRequisition(null);
        } catch (error) {
            toast.showApiError(error, 'Failed to delete requisition');
        }
    };

    const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
        fetchRequisitions(page - 1); // API uses 0-based indexing
    };

    const formatDate = (dateString: string) => {
        return format(parseISO(dateString), 'dd-MM-yyyy');
    };

    if (loading) {
        return <div data-testid="loading-state">Loading...</div>;
    }

    return (
        <div style={{ padding: '20px' }} data-help-page="store.myReqs.page">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h4">My Requisitions</Typography>
                <Button
                    data-help="store.myReqs.create"
                    variant="contained"
                    color="primary"
                    startIcon={<Add />}
                    onClick={() => navigate('/requisition-create')}
                >
                    Create Requisition
                </Button>
            </Box>

            <div style={{ marginBottom: '20px' }}>
                Total Requisitions: {pageInfo.totalElements}
            </div>

            <ScrollableTable>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px', minWidth: 640 }} data-testid="requisitions-table" data-help-section="store.myReqs.table">
                <thead>
                    <tr>
                        <th data-help="store.reqList.col.refNo" style={tableHeaderStyle}>Ref No</th>
                        <th data-help="store.reqList.col.date" style={tableHeaderStyle}>Date</th>
                        <th data-help="store.myReqs.col.status" style={tableHeaderStyle}>Status</th>
                        <th data-help="store.myReqs.col.actions" style={tableHeaderStyle}>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {requisitions.map((requisition) => (
                        <tr key={requisition.id} data-testid={`requisition-row-${requisition.id}`}>
                            <td style={tableCellStyle}>{requisition.refNo}</td>
                            <td style={tableCellStyle}>
                                {requisition.date ? formatDate(requisition.date) : '-'}
                            </td>
                            <td style={tableCellStyle}>
                                <Box
                                    component="span"
                                    sx={{
                                        display: 'inline-block',
                                        px: 1,
                                        py: 0.5,
                                        borderRadius: 1,
                                        bgcolor: requisition.status === 'NEW' ? 'warning.light' : 'success.light',
                                        color: requisition.status === 'NEW' ? 'warning.dark' : 'success.dark'
                                    }}
                                >
                                    {requisition.status ? REQUISITION_STATUS_LABELS[requisition.status] : '-'}
                                </Box>
                                {requisition.status === 'REJECTED' && requisition.rejectionReason && (
                                    <Box sx={{ mt: 0.5, fontSize: 13, color: 'error.main', maxWidth: 320 }}>
                                        Rejected: {requisition.rejectionReason}
                                    </Box>
                                )}
                            </td>
                            <td style={tableCellStyle}>
                                <Tooltip title="View Details" arrow>
                                    <IconButton
                                        size="small"
                                        onClick={() => navigate(`/requisition/${requisition.id}`)}
                                        color="primary"
                                        data-testid={`view-button-${requisition.id}`}
                                        data-help="store.myReqs.action.view"
                                        aria-label="View Details"
                                    >
                                        <Visibility />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={requisition.status !== 'NEW' ? 'Can only delete requisitions with New status' : 'Delete'} arrow>
                                    <span data-help="store.myReqs.delete" {...helpWhy(requisition.status !== 'NEW' ? { kind: 'status', message: 'Only a requisition with New status can be deleted.' } : null)}>
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                if (requisition.id) {
                                                    setSelectedRequisition(requisition.id);
                                                    setDeleteModalOpen(true);
                                                }
                                            }}
                                            color="error"
                                            disabled={requisition.status !== 'NEW'}
                                            data-testid={`delete-button-${requisition.id}`}
                                            aria-label="Delete"
                                        >
                                            <Delete />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            </ScrollableTable>

            {pageInfo.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination
                        count={pageInfo.totalPages}
                        page={pageInfo.currentPage + 1}
                        onChange={handlePageChange}
                        color="primary"
                        showFirstButton
                        showLastButton
                    />
                </Box>
            )}

            <Modal
                data-help-page="store.myReqs.deleteModal" open={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                aria-labelledby="delete-modal-title"
                aria-describedby="delete-modal-description"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 400,
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 1
                }}>
                    <Typography id="delete-modal-title" variant="h6" component="h2">
                        Confirm Delete
                    </Typography>
                    <Typography id="delete-modal-description" sx={{ mt: 2 }}>
                        Are you sure you want to delete this requisition?
                    </Typography>
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Button data-help="store.myReqs.deleteCancel" onClick={() => setDeleteModalOpen(false)}>Cancel</Button>
                        <Button data-help="store.myReqs.deleteConfirm" variant="contained" color="error" onClick={handleDelete}>
                            Delete
                        </Button>
                    </Box>
                </Box>
            </Modal>

        </div>
    );
};

export default MyRequisitions;
